import React, { useEffect, useMemo } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: 20,
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
};

const titleStyle = {
  fontSize: 30,
  fontWeight: 900,
  color: "#111827",
  marginBottom: 8,
};

const subStyle = {
  color: "#6b7280",
  fontSize: 15,
  marginBottom: 20,
};

const sectionStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 16,
  marginBottom: 16,
  background: "#fafafa",
};

const sectionTitleStyle = {
  fontSize: 18,
  fontWeight: 800,
  color: "#111827",
  marginBottom: 12,
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const summaryBox = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
};

const summaryLabel = {
  fontSize: 12,
  fontWeight: 700,
  color: "#6b7280",
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const summaryValue = {
  fontSize: 15,
  fontWeight: 700,
  color: "#111827",
  lineHeight: 1.4,
};

const infoBoxStyle = {
  border: "1px solid #dbeafe",
  background: "#eff6ff",
  color: "#1e3a8a",
  borderRadius: 12,
  padding: 12,
  fontSize: 14,
  lineHeight: 1.5,
  marginBottom: 16,
};

const statusHeroStyle = (meta) => ({
  border: `1px solid ${meta.border}`,
  background: meta.bg,
  color: meta.color,
  borderRadius: 14,
  padding: 14,
  marginBottom: 16,
});

const osnovaBoxStyle = {
  border: "1px solid #d1fae5",
  background: "#ecfdf5",
  color: "#065f46",
  borderRadius: 12,
  padding: 14,
  fontSize: 14,
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 12,
};

const grid1 = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 700,
  color: "#374151",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};


const recStatusOptions = [
  { value: "bez_podkladu", label: "Bez podkladů", color: "#991b1b", bg: "#fef2f2", border: "#fecaca" },
  { value: "rozpracovano", label: "Rozpracovaná", color: "#9a3412", bg: "#fff7ed", border: "#fdba74" },
  { value: "k_revizi", label: "K revizi", color: "#1d4ed8", bg: "#eff6ff", border: "#93c5fd" },
  { value: "hotovo", label: "Hotová", color: "#166534", bg: "#f0fdf4", border: "#86efac" },
];

const familyWorkflowOptions = [
  { value: "neodeslano", label: "Neodesláno", color: "#6b7280", bg: "#f3f4f6", border: "#d1d5db" },
  { value: "pripraveno_k_odeslani", label: "Připraveno k odeslání", color: "#1d4ed8", bg: "#eff6ff", border: "#93c5fd" },
  { value: "odeslano_rodine", label: "E-mail odeslán rodině", color: "#166534", bg: "#f0fdf4", border: "#86efac" },
  { value: "potvrzeno_rodinou", label: "Potvrzeno rodinou", color: "#166534", bg: "#ecfdf5", border: "#86efac" },
  { value: "uzamceno", label: "Uzamčeno", color: "#92400e", bg: "#fffbeb", border: "#fcd34d" },
];

function getFamilyWorkflowMeta(status) {
  return familyWorkflowOptions.find((x) => x.value === status) || familyWorkflowOptions[0];
}

function formatDateTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

function getRecStatusMeta(status) {
  return recStatusOptions.find((x) => x.value === status) || recStatusOptions[0];
}

const textareaStyle = {
  ...inputStyle,
  minHeight: 96,
  resize: "vertical",
};

const buttonRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginBottom: 12,
};

const primaryBtnStyle = {
  border: "1px solid #111827",
  background: "#111827",
  color: "#fff",
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

const secondaryBtnStyle = {
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111827",
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

function oneLine(...parts) {
  return parts
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join(" ");
}

function cleanText(v) {
  return String(v || "").trim();
}

function addLine(lines, label, value) {
  const v = cleanText(value);
  if (!v) return;
  lines.push(`${label}: ${v}`);
}

function addOsnovaBlock(blocks, title, values) {
  const filtered = (values || []).map(cleanText).filter(Boolean);
  if (!filtered.length) return;
  blocks.push(`${title}\n- ${filtered.join("\n- ")}`);
}

function joinParagraphs(parts) {
  return parts.map(cleanText).filter(Boolean).join("\n\n");
}

function buildSpeechHeaderBlock({
  jmenoZesnuleho,
  datumUmrti,
  datumObradu,
  casObradu,
  mistoObradu,
}) {
  const lines = [];

  if (cleanText(jmenoZesnuleho)) lines.push(`Zesnulý / zesnulá: ${cleanText(jmenoZesnuleho)}`);
  if (cleanText(datumUmrti)) lines.push(`Datum úmrtí: ${cleanText(datumUmrti)}`);

  const obradParts = [
    cleanText(datumObradu),
    cleanText(casObradu),
  ].filter(Boolean);

  if (obradParts.length) {
    lines.push(`Rozloučení: ${obradParts.join(" v ")}`);
  }

  if (cleanText(mistoObradu)) lines.push(`Místo rozloučení: ${cleanText(mistoObradu)}`);

  return lines.join("\n");
}

function prependSpeechHeader(header, body) {
  const cleanHeader = cleanText(header);
  const cleanBody = cleanText(body);

  if (!cleanHeader) return cleanBody;
  if (!cleanBody) return cleanHeader;

  if (cleanBody.startsWith(cleanHeader)) return cleanBody;

  return `${cleanHeader}\n\n${cleanBody}`;
}

function Field({ label, value, onChange, textarea = false, rows = 4, inputStyleOverride = null }) {
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      {textarea ? (
        <textarea
          rows={rows}
          style={{ ...textareaStyle, ...(inputStyleOverride || {}) }}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          style={{ ...inputStyle, ...(inputStyleOverride || {}) }}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div style={summaryBox}>
      <div style={summaryLabel}>{label}</div>
      <div style={summaryValue}>{value || "—"}</div>
    </div>
  );
}

function readToken() {
  try {
    return (
      localStorage.getItem("pohrebka_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("accessToken") ||
      ""
    );
  } catch {
    return "";
  }
}

export default function Pohrebka2RecForm() {
  const { order, patchSection } = usePohrebka2();

  const rec = order?.rec || {};
  const [printFontSize, setPrintFontSize] = React.useState(20);
  const [printLineHeight, setPrintLineHeight] = React.useState(1.6);
  const recStatus = rec?.stav || "bez_podkladu";
  const recStatusMeta = getRecStatusMeta(recStatus);
  const familyWorkflowStatus = rec?.stavRodina || "neodeslano";
  const familyWorkflowMeta = getFamilyWorkflowMeta(familyWorkflowStatus);

  const canPrepareFamilySend = familyWorkflowStatus === "neodeslano";
  const canMarkFamilyConfirmed =
    familyWorkflowStatus === "pripraveno_k_odeslani" && !rec?.uzamcenoPoPotvrzeni;
  const canLockFamilyWorkflow =
    familyWorkflowStatus === "potvrzeno_rodinou" && !rec?.uzamcenoPoPotvrzeni;
  const zesnula = order?.zesnulaOsoba || {};
  const vypravitel = order?.vypravitel || {};
  const obrad = order?.obrad || {};
  const vstupni = order?.vstupniInformace || {};

  const jmenoZesnuleho = oneLine(
    zesnula?.jmeno || vstupni?.jmenoZesnuleho,
    zesnula?.prijmeni || vstupni?.prijmeniZesnuleho
  );

  const datumNarozeni =
    zesnula?.datumNarozeni ||
    vstupni?.datumNarozeni ||
    vstupni?.datumNarozeniZesnuleho ||
    "";

  const datumUmrti =
    zesnula?.datumUmrti ||
    vstupni?.datumUmrti ||
    "";

  const mistoUmrti =
    zesnula?.mistoUmrti ||
    vstupni?.mistoUmrti ||
    "";

  const vypravitelCeleJmeno = oneLine(
    vypravitel?.jmeno,
    vypravitel?.prijmeni
  );

  const mistoObradu =
    obrad?.misto ||
    obrad?.krematorium ||
    obrad?.hrbitov ||
    "";

  const recnik = obrad?.recnik || "";

  const kontaktRodinaEmail = rec?.kontaktRodinaEmail || vypravitel?.email || "";
  const kontaktRodinaJmeno = rec?.kontaktRodinaJmeno || vypravitelCeleJmeno || "";
  const recShareToken = String(rec?.sdileniToken || "").trim();
  const recSharePath = recShareToken ? `/rec-rodina/${recShareToken}` : "";
  const recShareUrl = recSharePath ? `${window.location.origin}${recSharePath}` : "";

  const autoPoznamky = useMemo(() => {
    const lines = [];
    addLine(lines, "Zesnulý / zesnulá", jmenoZesnuleho);
    addLine(lines, "Datum narození", datumNarozeni);
    addLine(lines, "Datum úmrtí", datumUmrti);
    addLine(lines, "Místo úmrtí", mistoUmrti);
    addLine(lines, "Vypravitel", vypravitelCeleJmeno);
    addLine(lines, "Vztah k zesnulému", vypravitel?.vztah);
    addLine(lines, "Datum obřadu", obrad?.datum);
    addLine(lines, "Čas obřadu", obrad?.cas);
    addLine(lines, "Místo obřadu", mistoObradu);
    addLine(lines, "Řečník", recnik);
    addLine(lines, "Poznámka obřad", obrad?.poznamka);
    addLine(lines, "Poznámka vypravitel", vypravitel?.poznamka);
    addLine(lines, "Poznámka zesnulá osoba", zesnula?.poznamka);
    addLine(lines, "Poznámka vstupní informace", vstupni?.poznamka);
    return lines.join("\n");
  }, [
    jmenoZesnuleho,
    datumNarozeni,
    datumUmrti,
    mistoUmrti,
    vypravitelCeleJmeno,
    vypravitel?.vztah,
    vypravitel?.poznamka,
    obrad?.datum,
    obrad?.cas,
    obrad?.poznamka,
    mistoObradu,
    recnik,
    zesnula?.poznamka,
    vstupni?.poznamka,
  ]);

  const applyOrderBasics = () => {
    patchSection("rec", {
      ...rec,
      forma: {
        ...(rec?.forma || {}),
        delka: rec?.forma?.delka || "Krátká až střední",
        styl: rec?.forma?.styl || "Důstojný a lidský",
      },
      text: {
        ...(rec?.text || {}),
        poznamky: autoPoznamky,
      },
    });
  };


  const draftText = useMemo(() => {
    const pieces = [];

    if (jmenoZesnuleho) {
      pieces.push(`${jmenoZesnuleho}`);
    }

    const lifeParts = [];
    if (datumNarozeni) lifeParts.push(`se narodil(a) ${datumNarozeni}`);
    if (datumUmrti) lifeParts.push(`a zemřel(a) ${datumUmrti}`);
    if (lifeParts.length) {
      pieces.push(lifeParts.join(" "));
    }

    if (mistoUmrti) {
      pieces.push(`Místem úmrtí bylo: ${mistoUmrti}.`);
    }

    if (rec?.zivot?.puvod) pieces.push(`Původ / kořeny: ${rec.zivot.puvod}.`);
    if (rec?.zivot?.detstvi) pieces.push(`Dětství: ${rec.zivot.detstvi}.`);
    if (rec?.zivot?.skola) pieces.push(`Škola: ${rec.zivot.skola}.`);
    if (rec?.zivot?.prace) pieces.push(`Práce: ${rec.zivot.prace}.`);
    if (rec?.zivot?.zajmy) pieces.push(`Zájmy: ${rec.zivot.zajmy}.`);
    if (rec?.zivot?.hodnoty) pieces.push(`Hodnoty: ${rec.zivot.hodnoty}.`);

    const rodinaParts = [
      rec?.rodina?.partner ? `partner/partnerka: ${rec.rodina.partner}` : "",
      rec?.rodina?.deti ? `děti: ${rec.rodina.deti}` : "",
      rec?.rodina?.vnucata ? `vnoučata: ${rec.rodina.vnucata}` : "",
      rec?.rodina?.sourozenci ? `sourozenci: ${rec.rodina.sourozenci}` : "",
      rec?.rodina?.rodice ? `rodiče: ${rec.rodina.rodice}` : "",
      rec?.rodina?.dalsi ? `další blízcí: ${rec.rodina.dalsi}` : "",
    ].filter(Boolean);

    if (rodinaParts.length) {
      pieces.push(`Rodina a blízcí: ${rodinaParts.join(", ")}.`);
    }

    if (rec?.charakter?.povaha) pieces.push(`Povaha: ${rec.charakter.povaha}.`);
    if (rec?.charakter?.vlastnosti) pieces.push(`Výrazné vlastnosti: ${rec.charakter.vlastnosti}.`);
    if (rec?.charakter?.zvyky) pieces.push(`Typické zvyky: ${rec.charakter.zvyky}.`);
    if (rec?.charakter?.vzpominky) pieces.push(`Vzpomínky: ${rec.charakter.vzpominky}.`);
    if (rec?.charakter?.urciteZminit) pieces.push(`Určitě zmínit: ${rec.charakter.urciteZminit}.`);

    if (rec?.forma?.dulezityDuraz) pieces.push(`Důležitý důraz řeči: ${rec.forma.dulezityDuraz}.`);
    if (rec?.forma?.zaver) pieces.push(`Závěr: ${rec.forma.zaver}.`);

    return pieces.filter(Boolean).join("\n\n");
  }, [jmenoZesnuleho, datumNarozeni, datumUmrti, mistoUmrti, rec]);

  const osnovaText = useMemo(() => {
    const blocks = [];

    addOsnovaBlock(blocks, "1. Základní uvedení", [
      jmenoZesnuleho,
      datumNarozeni ? `Datum narození: ${datumNarozeni}` : "",
      datumUmrti ? `Datum úmrtí: ${datumUmrti}` : "",
      mistoUmrti ? `Místo úmrtí: ${mistoUmrti}` : "",
    ]);

    addOsnovaBlock(blocks, "2. Rodina a nejbližší", [
      rec?.rodina?.partner ? `Partner / partnerka: ${rec.rodina.partner}` : "",
      rec?.rodina?.deti ? `Děti: ${rec.rodina.deti}` : "",
      rec?.rodina?.vnucata ? `Vnoučata: ${rec.rodina.vnucata}` : "",
      rec?.rodina?.sourozenci ? `Sourozenci: ${rec.rodina.sourozenci}` : "",
      rec?.rodina?.rodice ? `Rodiče: ${rec.rodina.rodice}` : "",
      rec?.rodina?.dalsi ? `Další blízcí: ${rec.rodina.dalsi}` : "",
    ]);

    addOsnovaBlock(blocks, "3. Životní cesta", [
      rec?.zivot?.puvod ? `Původ: ${rec.zivot.puvod}` : "",
      rec?.zivot?.detstvi ? `Dětství: ${rec.zivot.detstvi}` : "",
      rec?.zivot?.skola ? `Škola: ${rec.zivot.skola}` : "",
      rec?.zivot?.prace ? `Práce: ${rec.zivot.prace}` : "",
      rec?.zivot?.zajmy ? `Zájmy: ${rec.zivot.zajmy}` : "",
      rec?.zivot?.hodnoty ? `Hodnoty: ${rec.zivot.hodnoty}` : "",
    ]);

    addOsnovaBlock(blocks, "4. Jaký člověk to byl", [
      rec?.charakter?.povaha ? `Povaha: ${rec.charakter.povaha}` : "",
      rec?.charakter?.vlastnosti ? `Vlastnosti: ${rec.charakter.vlastnosti}` : "",
      rec?.charakter?.zvyky ? `Zvyky: ${rec.charakter.zvyky}` : "",
      rec?.charakter?.vzpominky ? `Vzpomínky: ${rec.charakter.vzpominky}` : "",
      rec?.charakter?.urciteZminit ? `Určitě zmínit: ${rec.charakter.urciteZminit}` : "",
    ]);

    addOsnovaBlock(blocks, "5. Obřad a tón řeči", [
      obrad?.datum ? `Datum obřadu: ${obrad.datum}` : "",
      obrad?.cas ? `Čas obřadu: ${obrad.cas}` : "",
      mistoObradu ? `Místo obřadu: ${mistoObradu}` : "",
      recnik ? `Řečník: ${recnik}` : "",
      rec?.forma?.delka ? `Délka: ${rec.forma.delka}` : "",
      rec?.forma?.styl ? `Styl: ${rec.forma.styl}` : "",
      rec?.forma?.nabozenskyTon ? `Náboženský tón: ${rec.forma.nabozenskyTon}` : "",
      rec?.forma?.dulezityDuraz ? `Důležitý důraz: ${rec.forma.dulezityDuraz}` : "",
      rec?.forma?.zaver ? `Závěr: ${rec.forma.zaver}` : "",
    ]);

    addOsnovaBlock(blocks, "6. Citlivé body", [
      rec?.citlive?.nezminovat ? `Nezmiňovat: ${rec.citlive.nezminovat}` : "",
      rec?.citlive?.opatrne ? `Zmínit opatrně: ${rec.citlive.opatrne}` : "",
      rec?.citlive?.poznamka ? `Poznámka: ${rec.citlive.poznamka}` : "",
    ]);

    if (!blocks.length) {
      return "Osnova se začne skládat automaticky ve chvíli, kdy vyplníš údaje v této záložce nebo budou dostupná data ze zakázky.";
    }

    return blocks.join("\n\n");
  }, [
    jmenoZesnuleho,
    datumNarozeni,
    datumUmrti,
    mistoUmrti,
    rec,
    obrad,
    mistoObradu,
    recnik,
  ]);

  useEffect(() => {
    const current = cleanText(rec?.text?.poznamky);
    const auto = cleanText(autoPoznamky);
    if (current || !auto) return;

    patchSection("rec", {
      ...rec,
      text: {
        ...(rec?.text || {}),
        poznamky: auto,
      },
    });
  }, [autoPoznamky]);

  useEffect(() => {
    const nextEmail = cleanText(rec?.kontaktRodinaEmail) || cleanText(vypravitel?.email);
    const nextJmeno = cleanText(rec?.kontaktRodinaJmeno) || cleanText(vypravitelCeleJmeno);

    const hasOwnEmail = cleanText(rec?.kontaktRodinaEmail);
    const hasOwnJmeno = cleanText(rec?.kontaktRodinaJmeno);

    if ((hasOwnEmail || !nextEmail) && (hasOwnJmeno || !nextJmeno)) return;

    patchSection("rec", {
      ...rec,
      kontaktRodinaEmail: hasOwnEmail ? rec?.kontaktRodinaEmail : nextEmail,
      kontaktRodinaJmeno: hasOwnJmeno ? rec?.kontaktRodinaJmeno : nextJmeno,
    });
  }, [vypravitel?.email, vypravitelCeleJmeno]);

  const updateGroup = (group, key, value) => {
    patchSection("rec", {
      ...rec,
      [group]: {
        ...(rec?.[group] || {}),
        [key]: value,
      },
    });
  };

  const dalsiHudbaList = Array.isArray(rec?.hudba?.dalsiSeznam)
    ? rec.hudba.dalsiSeznam
    : (cleanText(rec?.hudba?.dalsi) ? [rec.hudba.dalsi] : []);

  const setDalsiHudbaList = (nextList) => {
    const normalized = (Array.isArray(nextList) ? nextList : []).map((x) => String(x || ""));
    patchSection("rec", {
      ...rec,
      hudba: {
        ...(rec?.hudba || {}),
        dalsiSeznam: normalized,
        dalsi: normalized.map((x) => cleanText(x)).filter(Boolean).join("\n"),
      },
    });
  };

  const addDalsiHudba = () => {
    setDalsiHudbaList([...(dalsiHudbaList || []), ""]);
  };

  const updateDalsiHudbaItem = (index, value) => {
    const next = [...dalsiHudbaList];
    next[index] = value;
    setDalsiHudbaList(next);
  };

  const removeDalsiHudbaItem = (index) => {
    const next = dalsiHudbaList.filter((_, i) => i !== index);
    setDalsiHudbaList(next);
  };

  const updateStatus = (value) => {
    patchSection("rec", {
      ...rec,
      stav: value,
    });
  };

  const setQuickStatus = (value) => {
    if (recStatus === value) return;
    updateStatus(value);
  };

  const speechHeaderBlock = buildSpeechHeaderBlock({
    jmenoZesnuleho,
    datumUmrti,
    datumObradu: obrad?.datum || "",
    casObradu: obrad?.cas || "",
    mistoObradu,
  });

  const fillDraft = () => {
    const notesText = cleanText(rec?.text?.poznamky || "");
    const currentDraft = cleanText(rec?.text?.navrhReci || "");
    const baseText = currentDraft || notesText;

    patchSection("rec", {
      ...rec,
      text: {
        ...(rec?.text || {}),
        navrhReci: prependSpeechHeader(speechHeaderBlock, baseText),
      },
    });
  };

  const clearDraft = () => {
    patchSection("rec", {
      ...rec,
      text: {
        ...(rec?.text || {}),
        navrhReci: "",
      },
    });
  };

  const printHudbaOverview = () => {
    const hudbaNastup = cleanText(rec?.hudba?.nastup);
    const hudbaPred = cleanText(rec?.hudba?.predProslovem);
    const hudbaZaver = cleanText(rec?.hudba?.zaver);
    const hudbaDalsi = Array.isArray(rec?.hudba?.dalsiSeznam)
      ? rec.hudba.dalsiSeznam.map((x) => cleanText(x)).filter(Boolean)
      : (cleanText(rec?.hudba?.dalsi) ? [cleanText(rec?.hudba?.dalsi)] : []);

    const hasAnything = hudbaNastup || hudbaPred || hudbaZaver || hudbaDalsi.length;
    if (!hasAnything) {
      window.alert("Hudba k obřadu je zatím prázdná.");
      return;
    }

    const title = jmenoZesnuleho || "Hudba k obřadu";
    const obradDatum = String(obrad?.datum || "").trim();
    const obradCas = String(obrad?.cas || "").trim();
    const obradMisto = String(mistoObradu || "").trim();

    const meta = [
      obradDatum ? `Datum obřadu: ${obradDatum}` : "",
      obradCas ? `Čas obřadu: ${obradCas}` : "",
      obradMisto ? `Místo obřadu: ${obradMisto}` : "",
      recnik ? `Řečník: ${recnik}` : "",
    ].filter(Boolean).join(" • ");

    const sections = [];
    if (hudbaNastup) sections.push({ label: "Nástup", value: hudbaNastup });
    if (hudbaPred) sections.push({ label: "Před proslovem", value: hudbaPred });
    if (hudbaZaver) sections.push({ label: "Po proslovu / závěr", value: hudbaZaver });
    hudbaDalsi.forEach((x, i) => sections.push({ label: `Další hudba ${i + 1}`, value: x }));

    const esc = (v) =>
      String(v || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const escapedTitle = esc(title);
    const escapedMeta = esc(meta);
    const rowsHtml = sections.map((item) => `
      <div class="row">
        <div class="phase">${esc(item.label)}</div>
        <div class="value">${esc(item.value)}</div>
      </div>
    `).join("");

    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    frame.setAttribute("aria-hidden", "true");
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(frame);
      window.alert("Nepodařilo se připravit tisk hudby.");
      return;
    }

    doc.open();
    doc.write(`<!doctype html>
<html lang="cs">
<head>
  <meta charset="utf-8" />
  <title>Hudba k obřadu – ${escapedTitle}</title>
  <style>
    @page { size: A4; margin: 16mm; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: Arial, sans-serif;
    }
    .page {
      padding: 16mm;
      box-sizing: border-box;
    }
    .title {
      font-size: 24px;
      font-weight: 800;
      margin-bottom: 10px;
    }
    .subtitle {
      font-size: 18px;
      font-weight: 700;
      margin-bottom: 8px;
    }
    .meta {
      font-size: 13px;
      color: #4b5563;
      margin-bottom: 18px;
    }
    .list {
      display: grid;
      gap: 10px;
    }
    .row {
      border: 1px solid #d1d5db;
      border-radius: 10px;
      padding: 10px 12px;
      background: #f9fafb;
    }
    .phase {
      font-size: 12px;
      font-weight: 800;
      color: #6b7280;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    .value {
      font-size: 18px;
      font-weight: 700;
      line-height: 1.5;
      white-space: pre-wrap;
    }
    @media print {
      .page { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="title">${escapedTitle}</div>
    <div class="subtitle">Hudba k obřadu</div>
    <div class="meta">${escapedMeta}</div>
    <div class="list">${rowsHtml}</div>
  </div>
</body>
</html>`);
    doc.close();

    window.setTimeout(() => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } finally {
        window.setTimeout(() => {
          try {
            document.body.removeChild(frame);
          } catch {}
        }, 1000);
      }
    }, 250);
  };

  const printSpeech = () => {
    const printHudbaList = [];
    if (cleanText(rec?.hudba?.nastup)) printHudbaList.push(`Nástup: ${cleanText(rec?.hudba?.nastup)}`);
    if (cleanText(rec?.hudba?.predProslovem)) printHudbaList.push(`Před proslovem: ${cleanText(rec?.hudba?.predProslovem)}`);
    if (cleanText(rec?.hudba?.zaver)) printHudbaList.push(`Po proslovu / závěr: ${cleanText(rec?.hudba?.zaver)}`);
    const _printDalsiHudba = Array.isArray(rec?.hudba?.dalsiSeznam)
      ? rec.hudba.dalsiSeznam.filter((x) => cleanText(x))
      : (cleanText(rec?.hudba?.dalsi) ? [cleanText(rec?.hudba?.dalsi)] : []);
    _printDalsiHudba.forEach((x, i) => printHudbaList.push(`Další hudba ${i + 1}: ${x}`));
    const speechText = prependSpeechHeader(
      speechHeaderBlock,
      String(rec?.text?.navrhReci || "").trim()
    );
    if (!speechText) {
      window.alert("Návrh řeči je prázdný.");
      return;
    }

    const title = jmenoZesnuleho || "Řeč";
    const obradDatum = String(obrad?.datum || "").trim();
    const obradCas = String(obrad?.cas || "").trim();
    const obradMisto = String(mistoObradu || "").trim();

    const meta = [
      obradDatum ? `Datum obřadu: ${obradDatum}` : "",
      obradCas ? `Čas obřadu: ${obradCas}` : "",
      obradMisto ? `Místo obřadu: ${obradMisto}` : "",
      recnik ? `Řečník: ${recnik}` : "",
    ].filter(Boolean).join(" • ");

    const escapedTitle = title
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    const escapedMeta = meta
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    const escapedHudba = printHudbaList
      .map((x) =>
        String(x)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
      )
      .join("<br>");

    const escapedSpeech = speechText
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "<br>");

    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    frame.setAttribute("aria-hidden", "true");
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(frame);
      window.alert("Nepodařilo se připravit tisk.");
      return;
    }

    doc.open();
    doc.write(`<!doctype html>
<html lang="cs">
<head>
  <meta charset="utf-8" />
  <title>Tisk řeči – ${escapedTitle}</title>
  <style>
    @page { size: A4; margin: 18mm; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: Arial, sans-serif;
    }
    .page {
      padding: 18mm;
      box-sizing: border-box;
    }
    .title {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 10px;
    }
    .meta {
      font-size: 13px;
      color: #444;
      margin-bottom: 16px;
    }
    .hudba {
      margin-bottom: 20px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #f9fafb;
      font-size: 14px;
      line-height: 1.6;
    }
    .hudba-title {
      font-size: 12px;
      font-weight: 800;
      color: #6b7280;
      text-transform: uppercase;
      margin-bottom: 6px;
    }
    .speech {
      font-size: ${printFontSize}px;
      line-height: ${printLineHeight};
      white-space: normal;
    }
    @media print {
      .page {
        padding: 0;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="title">${escapedTitle}</div>
    <div class="meta">${escapedMeta}</div>
    ${escapedHudba ? `<div class="hudba"><div class="hudba-title">Hudba k obřadu</div>${escapedHudba}</div>` : ""}
    <div class="speech">${escapedSpeech}</div>
  </div>
</body>
</html>`);
    doc.close();

    window.setTimeout(() => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } finally {
        window.setTimeout(() => {
          try {
            document.body.removeChild(frame);
          } catch {}
        }, 1000);
      }
    }, 250);
  };

  const appendToNotes = (block) => {
    const current = cleanText(rec?.text?.poznamky);
    const next = joinParagraphs([current, block]);

    patchSection("rec", {
      ...rec,
      text: {
        ...(rec?.text || {}),
        poznamky: next,
      },
    });
  };

  const fillFamilyLifeNotes = () => {
    const block = joinParagraphs([
      rec?.rodina?.partner ? `Partner / partnerka: ${rec.rodina.partner}` : "",
      rec?.rodina?.deti ? `Děti: ${rec.rodina.deti}` : "",
      rec?.rodina?.vnucata ? `Vnoučata: ${rec.rodina.vnucata}` : "",
      rec?.rodina?.sourozenci ? `Sourozenci: ${rec.rodina.sourozenci}` : "",
      rec?.rodina?.rodice ? `Rodiče: ${rec.rodina.rodice}` : "",
      rec?.rodina?.dalsi ? `Další blízcí: ${rec.rodina.dalsi}` : "",
      rec?.zivot?.puvod ? `Původ: ${rec.zivot.puvod}` : "",
      rec?.zivot?.detstvi ? `Dětství: ${rec.zivot.detstvi}` : "",
      rec?.zivot?.skola ? `Škola: ${rec.zivot.skola}` : "",
      rec?.zivot?.prace ? `Práce: ${rec.zivot.prace}` : "",
      rec?.zivot?.zajmy ? `Zájmy: ${rec.zivot.zajmy}` : "",
      rec?.zivot?.hodnoty ? `Hodnoty: ${rec.zivot.hodnoty}` : "",
    ]);

    if (!block) return;
    appendToNotes(block);
  };

  const fillCharacterSensitiveNotes = () => {
    const block = joinParagraphs([
      rec?.charakter?.povaha ? `Povaha: ${rec.charakter.povaha}` : "",
      rec?.charakter?.vlastnosti ? `Vlastnosti: ${rec.charakter.vlastnosti}` : "",
      rec?.charakter?.zvyky ? `Zvyky: ${rec.charakter.zvyky}` : "",
      rec?.charakter?.vzpominky ? `Vzpomínky: ${rec.charakter.vzpominky}` : "",
      rec?.charakter?.urciteZminit ? `Určitě zmínit: ${rec.charakter.urciteZminit}` : "",
      rec?.citlive?.nezminovat ? `Nezmiňovat: ${rec.citlive.nezminovat}` : "",
      rec?.citlive?.opatrne ? `Zmínit opatrně: ${rec.citlive.opatrne}` : "",
      rec?.citlive?.poznamka ? `Poznámka: ${rec.citlive.poznamka}` : "",
    ]);

    if (!block) return;
    appendToNotes(block);
  };

  const currentRecOrderId = String(
    order?.id || order?.orderId || order?.zakazkaId || currentOrderId || ""
  ).trim();

  const applyRecApiPayload = (data) => {
    if (data?.rec?.rec) {
      patchSection("rec", {
        ...rec,
        ...data.rec.rec,
        sdileniToken: data?.token || data.rec.rec?.sdileniToken || rec?.sdileniToken || "",
        publicPath: data?.publicPath || rec?.publicPath || "",
      });
      return;
    }

    if (data?.rec) {
      patchSection("rec", {
        ...rec,
        ...data.rec,
        sdileniToken: data?.token || data.rec?.sdileniToken || rec?.sdileniToken || "",
        publicPath: data?.publicPath || rec?.publicPath || "",
      });
      return;
    }
  };

  const refreshFamilyWorkflowFromApi = async (silent = false) => {
    if (!currentRecOrderId) {
      if (!silent) window.alert("Zakázka nemá ID. Nejdřív ji ulož.");
      return;
    }

    try {
      const token = readToken();
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`/api/pohrebka2/orders/${encodeURIComponent(currentRecOrderId)}`, {
        method: "GET",
        headers,
        credentials: "include",
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Nepodařilo se načíst aktuální stav zakázky.");
      }

      const item = data?.item || {};
      const latestRec =
        item?.rec?.rec ||
        item?.rec ||
        item?.data?.rec?.rec ||
        item?.data?.rec ||
        {};

      patchSection("rec", {
        ...rec,
        ...latestRec,
        sdileniToken:
          latestRec?.sdileniToken ||
          item?.rec?.sdileniToken ||
          rec?.sdileniToken ||
          "",
        publicPath:
          latestRec?.publicPath ||
          item?.publicPath ||
          rec?.publicPath ||
          "",
      });

      if (!silent) window.alert("Stav řeči byl obnoven z API.");
    } catch (e) {
      if (!silent) window.alert(e?.message || "Chyba při načtení aktuálního stavu.");
    }
  };


  useEffect(() => {
    if (!currentRecOrderId) return;
    refreshFamilyWorkflowFromApi(true);
  }, [currentRecOrderId]);

  const prepareForFamilySend = async () => {
    if (!canPrepareFamilySend) return;

    if (!currentRecOrderId) {
      window.alert("Zakázka nemá ID. Nejdřív ji ulož.");
      return;
    }

    try {
      const token = readToken();
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch("/api/pohrebka2/rec/share/create", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ orderId: currentRecOrderId }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Nepodařilo se připravit sdílení řeči.");
      }

      applyRecApiPayload(data);
      window.alert("Sdílení pro rodinu je připravené.");
    } catch (e) {
      window.alert(e?.message || "Chyba při přípravě sdílení.");
    }
  };

  const sendFamilyEmail = async () => {
    if (!currentRecOrderId) {
      window.alert("Zakázka nemá ID. Nejdřív ji ulož.");
      return;
    }

    try {
      const token = readToken();
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`/api/pohrebka2/rec/share/email/send/${encodeURIComponent(currentRecOrderId)}`, {
        method: "POST",
        headers,
        credentials: "include",
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Nepodařilo se odeslat e-mail rodině.");
      }

      applyRecApiPayload(data);
      window.alert("E-mail rodině byl odeslán.");
    } catch (e) {
      window.alert(e?.message || "Chyba při odeslání e-mailu.");
    }
  };

  const markFamilyConfirmed = async () => {
    if (!canMarkFamilyConfirmed) return;

    if (!currentRecOrderId) {
      window.alert("Zakázka nemá ID. Nejdřív ji ulož.");
      return;
    }

    try {
      const token = readToken();
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch("/api/pohrebka2/rec/share/confirm", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ orderId: currentRecOrderId }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Nepodařilo se potvrdit podklady rodinou.");
      }

      applyRecApiPayload(data);
      window.alert("Rodina byla označena jako potvrzená.");
    } catch (e) {
      window.alert(e?.message || "Chyba při potvrzení rodinou.");
    }
  };

  const lockAfterFamilyConfirmation = async () => {
    if (!canLockFamilyWorkflow) return;

    if (!currentRecOrderId) {
      window.alert("Zakázka nemá ID. Nejdřív ji ulož.");
      return;
    }

    try {
      const token = readToken();
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch("/api/pohrebka2/rec/share/lock", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ orderId: currentRecOrderId }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Nepodařilo se uzamknout sdílení.");
      }

      applyRecApiPayload(data);
      window.alert("Sdílení bylo uzamčeno.");
    } catch (e) {
      window.alert(e?.message || "Chyba při uzamčení sdílení.");
    }
  };

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>Řeč / řečnice</div>
      <div style={subStyle}>
        Podklady pro přípravu smuteční řeči. Přehledně, jednoduše a prakticky.
      </div>

      <div style={statusHeroStyle(recStatusMeta)}>
        <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.8, textTransform: "uppercase", marginBottom: 6 }}>
          Aktuální stav řeči
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.1 }}>
              {recStatusMeta.label}
            </div>
            <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.5 }}>
              {recStatus === "bez_podkladu" ? "Zatím nejsou vyplněné podklady k řeči." : ""}
              {recStatus === "rozpracovano" ? "Podklady se průběžně doplňují a řeč se připravuje." : ""}
              {recStatus === "k_revizi" ? "Návrh je připravený ke kontrole nebo doladění." : ""}
              {recStatus === "hotovo" ? "Řeč je dokončená a připravená k použití." : ""}
            </div>
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              padding: "8px 12px",
              fontWeight: 800,
              background: "#ffffff",
              color: recStatusMeta.color,
              border: `1px solid ${recStatusMeta.border}`,
            }}
          >
            Stav: {recStatusMeta.label}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <button type="button" style={secondaryBtnStyle} onClick={() => setQuickStatus("rozpracovano")}>
            Označit jako rozpracované
          </button>
          <button type="button" style={secondaryBtnStyle} onClick={() => setQuickStatus("k_revizi")}>
            Přesunout k revizi
          </button>
          <button type="button" style={primaryBtnStyle} onClick={() => setQuickStatus("hotovo")}>
            Označit jako hotové
          </button>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Automaticky převzaté údaje ze zakázky</div>
        <div style={infoBoxStyle}>
          Pole <strong>Poznámky pro řečnici</strong> se při otevření zakázky samo předvyplní základními údaji.
          Níže si pak můžeš jedním klikem doplnit rodinu, život, charakter nebo vytvořit hrubý návrh řeči.
        </div>
        <div style={summaryGrid}>
          <SummaryItem label="Zesnulý / zesnulá" value={jmenoZesnuleho} />
          <SummaryItem label="Datum narození" value={datumNarozeni} />
          <SummaryItem label="Datum úmrtí" value={datumUmrti} />
          <SummaryItem label="Místo úmrtí" value={mistoUmrti} />
          <SummaryItem label="Vypravitel" value={vypravitelCeleJmeno} />
          <SummaryItem label="Řečník" value={recnik} />
          <SummaryItem label="Datum obřadu" value={obrad?.datum || ""} />
          <SummaryItem label="Čas obřadu" value={obrad?.cas || ""} />
          <SummaryItem label="Místo obřadu" value={mistoObradu} />
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Osnova řeči</div>
        <div style={osnovaBoxStyle}>{osnovaText}</div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Rodina</div>
        <div style={grid2}>
          <Field label="Partner / partnerka" value={rec?.rodina?.partner} onChange={(v) => updateGroup("rodina", "partner", v)} />
          <Field label="Děti" value={rec?.rodina?.deti} onChange={(v) => updateGroup("rodina", "deti", v)} />
          <Field label="Vnoučata" value={rec?.rodina?.vnucata} onChange={(v) => updateGroup("rodina", "vnucata", v)} />
          <Field label="Sourozenci" value={rec?.rodina?.sourozenci} onChange={(v) => updateGroup("rodina", "sourozenci", v)} />
          <Field label="Rodiče" value={rec?.rodina?.rodice} onChange={(v) => updateGroup("rodina", "rodice", v)} />
          <Field label="Další blízcí" value={rec?.rodina?.dalsi} onChange={(v) => updateGroup("rodina", "dalsi", v)} />
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Život</div>
        <div style={grid2}>
          <Field label="Původ" value={rec?.zivot?.puvod} onChange={(v) => updateGroup("zivot", "puvod", v)} />
          <Field label="Dětství" value={rec?.zivot?.detstvi} onChange={(v) => updateGroup("zivot", "detstvi", v)} />
          <Field label="Škola" value={rec?.zivot?.skola} onChange={(v) => updateGroup("zivot", "skola", v)} />
          <Field label="Práce" value={rec?.zivot?.prace} onChange={(v) => updateGroup("zivot", "prace", v)} />
          <Field label="Zájmy" value={rec?.zivot?.zajmy} onChange={(v) => updateGroup("zivot", "zajmy", v)} />
          <Field label="Hodnoty" value={rec?.zivot?.hodnoty} onChange={(v) => updateGroup("zivot", "hodnoty", v)} />
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Charakter a vzpomínky</div>
        <div style={grid1}>
          <Field label="Povaha" value={rec?.charakter?.povaha} onChange={(v) => updateGroup("charakter", "povaha", v)} textarea />
          <Field label="Vlastnosti" value={rec?.charakter?.vlastnosti} onChange={(v) => updateGroup("charakter", "vlastnosti", v)} textarea />
          <Field label="Zvyky" value={rec?.charakter?.zvyky} onChange={(v) => updateGroup("charakter", "zvyky", v)} textarea />
          <Field label="Vzpomínky" value={rec?.charakter?.vzpominky} onChange={(v) => updateGroup("charakter", "vzpominky", v)} textarea />
          <Field label="Určitě zmínit" value={rec?.charakter?.urciteZminit} onChange={(v) => updateGroup("charakter", "urciteZminit", v)} textarea />
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Citlivé body</div>
        <div style={grid1}>
          <Field label="Nezmiňovat" value={rec?.citlive?.nezminovat} onChange={(v) => updateGroup("citlive", "nezminovat", v)} textarea />
          <Field label="Zmínit opatrně" value={rec?.citlive?.opatrne} onChange={(v) => updateGroup("citlive", "opatrne", v)} textarea />
          <Field label="Poznámka" value={rec?.citlive?.poznamka} onChange={(v) => updateGroup("citlive", "poznamka", v)} textarea />
        </div>
      </div>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Forma řeči</div>
          <div style={grid2}>
            <Field label="Délka" value={rec?.forma?.delka} onChange={(v) => updateGroup("forma", "delka", v)} />
            <Field label="Styl" value={rec?.forma?.styl} onChange={(v) => updateGroup("forma", "styl", v)} />
            <Field label="Náboženský tón" value={rec?.forma?.nabozenskyTon} onChange={(v) => updateGroup("forma", "nabozenskyTon", v)} />
            <Field label="Důležitý důraz" value={rec?.forma?.dulezityDuraz} onChange={(v) => updateGroup("forma", "dulezityDuraz", v)} />
            <Field label="Závěr" value={rec?.forma?.zaver} onChange={(v) => updateGroup("forma", "zaver", v)} />
          </div>

        </div>

          <div style={sectionStyle}>
            
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Hudba k obřadu</div>
        <div style={grid2}>
          <Field
            label="Nástup"
            value={rec?.hudba?.nastup}
            onChange={(v) => updateGroup("hudba", "nastup", v)}
          />
          <Field
            label="Před proslovem"
            value={rec?.hudba?.predProslovem}
            onChange={(v) => updateGroup("hudba", "predProslovem", v)}
          />
          <Field
            label="Po proslovu / závěr"
            value={rec?.hudba?.zaver}
            onChange={(v) => updateGroup("hudba", "zaver", v)}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Další hudba</div>

          <div style={{ display: "grid", gap: 10 }}>
            {dalsiHudbaList.map((item, index) => (
              <div
                key={`dalsi-hudba-${index}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 10,
                  alignItems: "start",
                }}
              >
                <Field
                  label={`Další hudba ${index + 1}`}
                  value={item}
                  onChange={(v) => updateDalsiHudbaItem(index, v)}
                />
                <button
                  type="button"
                  style={{ ...secondaryBtnStyle, marginTop: 26 }}
                  onClick={() => removeDalsiHudbaItem(index)}
                >
                  Odebrat
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              style={secondaryBtnStyle}
              onClick={addDalsiHudba}
            >
              + Přidat další hudbu
            </button>
          </div>
        </div>
      </div>

        <div style={sectionTitleStyle}>Rodina / sdílení řeči</div>

            <div style={grid2}>
              <Field
                label="Jméno kontaktu pro rodinu"
                value={kontaktRodinaJmeno}
                onChange={(v) => patchSection("rec", { ...rec, kontaktRodinaJmeno: v })}
              />
              <Field
                label="E-mail pro odeslání rodině"
                value={kontaktRodinaEmail}
                onChange={(v) => patchSection("rec", { ...rec, kontaktRodinaEmail: v })}
              />
            </div>

            <div style={{ ...summaryGrid, marginTop: 12 }}>
              <SummaryItem label="Stav pro rodinu" value={familyWorkflowMeta.label} />
              <SummaryItem label="Potvrzeno rodinou" value={rec?.potvrzenoRodinou ? "Ano" : "Ne"} />
              <SummaryItem label="Uzamčeno po potvrzení" value={rec?.uzamcenoPoPotvrzeni ? "Ano" : "Ne"} />
              <SummaryItem label="Poslední úprava rodinou" value={formatDateTime(rec?.posledniUpravaRodinou)} />
                <SummaryItem label="Sdílení vytvořeno" value={formatDateTime(rec?.sdileniVytvoreno)} />
              <SummaryItem label="E-mail odeslán dne" value={formatDateTime(rec?.sdileniNaposledOdeslano)} />
              <SummaryItem label="Potvrzeno dne" value={formatDateTime(rec?.datumPotvrzeniRodinou)} />
                <SummaryItem label="Uzamčeno dne" value={formatDateTime(rec?.datumUzamceniRodinou)} />
            </div>

            <div
              style={{
                marginTop: 12,
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "8px 12px",
                fontWeight: 800,
                background: familyWorkflowMeta.bg,
                color: familyWorkflowMeta.color,
                border: `1px solid ${familyWorkflowMeta.border}`,
              }}
            >
              Stav sdílení: {familyWorkflowMeta.label}
            </div>

            <div style={{ ...infoBoxStyle, marginTop: 12 }}>
              Tohle je zatím interní příprava pro budoucí odeslání podkladů rodině e-mailem.
              Každý krok už nese datum a čas. Po potvrzení rodinou bude možné přístup uzamknout
              a další úpravy povolit jen novým odesláním z aplikace.
            </div>

              {recShareUrl ? (
                <div
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#ffffff",
                    borderRadius: 12,
                    padding: 12,
                    marginTop: 12,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 6, textTransform: "uppercase" }}>
                    Veřejný odkaz pro rodinu
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", wordBreak: "break-all" }}>
                    {recShareUrl}
                  </div>

                  <div style={{ ...buttonRowStyle, marginTop: 10, marginBottom: 0 }}>
                    <button
                      type="button"
                      style={secondaryBtnStyle}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(recShareUrl);
                          window.alert("Odkaz byl zkopírován.");
                        } catch (e) {
                          window.alert("Kopírování odkazu se nepodařilo.");
                        }
                      }}
                    >
                      Kopírovat odkaz
                    </button>

                    <button
                      type="button"
                      style={secondaryBtnStyle}
                      onClick={() => window.open(recShareUrl, "_blank", "noopener,noreferrer")}
                    >
                      Otevřít odkaz
                    </button>
                  </div>
                </div>
              ) : null}


            <div style={{ ...buttonRowStyle, marginTop: 12, marginBottom: 0 }}>
              <button
                type="button"
                style={{ ...primaryBtnStyle, opacity: canPrepareFamilySend ? 1 : 0.5, cursor: canPrepareFamilySend ? "pointer" : "not-allowed" }}
                onClick={prepareForFamilySend}
                disabled={!canPrepareFamilySend}
              >
                Připravit k odeslání
              </button>
              <button
                type="button"
                style={{
                  ...secondaryBtnStyle,
                  opacity: rec?.sdileniToken && kontaktRodinaEmail ? 1 : 0.5,
                  cursor: rec?.sdileniToken && kontaktRodinaEmail ? "pointer" : "not-allowed",
                  background: familyWorkflowStatus === "odeslano_rodine" ? "#dcfce7" : secondaryBtnStyle.background,
                  border: familyWorkflowStatus === "odeslano_rodine" ? "1px solid #86efac" : secondaryBtnStyle.border,
                  color: familyWorkflowStatus === "odeslano_rodine" ? "#166534" : secondaryBtnStyle.color,
                  fontWeight: familyWorkflowStatus === "odeslano_rodine" ? 800 : secondaryBtnStyle.fontWeight,
                }}
                onClick={sendFamilyEmail}
                disabled={!rec?.sdileniToken || !kontaktRodinaEmail}
              >
                {familyWorkflowStatus === "odeslano_rodine" ? "E-mail rodině odeslán" : "Odeslat e-mail rodině"}
              </button>
              <button
                type="button"
                style={secondaryBtnStyle}
                onClick={() => refreshFamilyWorkflowFromApi(false)}
              >
                Obnovit stav z API
              </button>
              <button
                type="button"
                style={{ ...secondaryBtnStyle, opacity: canMarkFamilyConfirmed ? 1 : 0.5, cursor: canMarkFamilyConfirmed ? "pointer" : "not-allowed" }}
                onClick={markFamilyConfirmed}
                disabled={!canMarkFamilyConfirmed}
              >
                Označit jako potvrzeno rodinou
              </button>
              <button
                type="button"
                style={{ ...secondaryBtnStyle, opacity: canLockFamilyWorkflow ? 1 : 0.5, cursor: canLockFamilyWorkflow ? "pointer" : "not-allowed" }}
                onClick={lockAfterFamilyConfirmation}
                disabled={!canLockFamilyWorkflow}
              >
                Uzamknout po potvrzení
              </button>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Text</div>

            <div style={buttonRowStyle}>
              <button type="button" style={primaryBtnStyle} onClick={fillDraft}>
                Doplnit hlavičku a základ do návrhu řeči
              </button>
              <button type="button" style={secondaryBtnStyle} onClick={fillFamilyLifeNotes}>
                Převzít rodinu a život do poznámek
              </button>
              <button type="button" style={secondaryBtnStyle} onClick={fillCharacterSensitiveNotes}>
                Převzít charakter a citlivé body do poznámek
              </button>
              <button type="button" style={secondaryBtnStyle} onClick={clearDraft}>
                Vyčistit návrh řeči
              </button>
            </div>

            <div style={{ ...summaryGrid, marginBottom: 12 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={labelStyle}>Velikost textu pro tisk</span>
                <select
                  style={inputStyle}
                  value={String(printFontSize)}
                  onChange={(e) => setPrintFontSize(Number(e.target.value))}
                >
                  <option value="16">16 px</option>
                  <option value="18">18 px</option>
                  <option value="20">20 px</option>
                  <option value="22">22 px</option>
                  <option value="24">24 px</option>
                </select>
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={labelStyle}>Řádkování pro tisk</span>
                <select
                  style={inputStyle}
                  value={String(printLineHeight)}
                  onChange={(e) => setPrintLineHeight(Number(e.target.value))}
                >
                  <option value="1.3">1.3</option>
                  <option value="1.5">1.5</option>
                  <option value="1.6">1.6</option>
                  <option value="1.8">1.8</option>
                  <option value="2">2.0</option>
                </select>
              </label>

              <div style={{ display: "flex", alignItems: "end", gap: 10, flexWrap: "wrap" }}>
                <button type="button" style={primaryBtnStyle} onClick={printSpeech}>
                  Tisk řeči
                </button>
                <button type="button" style={secondaryBtnStyle} onClick={printHudbaOverview}>
                  Tisk hudby k obřadu
                </button>
              </div>
            </div>

            <div style={grid1}>
              <Field
                label="Poznámky pro řečnici"
                value={rec?.text?.poznamky}
                onChange={(v) => updateGroup("text", "poznamky", v)}
                textarea
                rows={8}
              />
              <Field
                label="Návrh řeči"
                value={rec?.text?.navrhReci}
                onChange={(v) => updateGroup("text", "navrhReci", v)}
                textarea
                rows={12}
                inputStyleOverride={{
                  fontSize: `${printFontSize}px`,
                  lineHeight: String(printLineHeight),
                }}
              />
            </div>
          </div>
        </div>
      );
    }
